import { canonicalAliases } from "@/config/urlHierarchy";

/**
 * Interna länkar i importerat innehåll (artiklar, bolagssidor) pekar ofta på
 * gamla URL:er som numera omdirigeras – t.ex. https://www.elmarknad.se/elavtal/jamfor-elavtal.
 * Varje sådan länk kostar en omdirigering (och www-varianten en 302) för både
 * besökare och sökmotorer. Här skrivs de om till slutdestinationen.
 */

/**
 * Gamla sökvägar -> slutdestination, utöver canonicalAliases. Målen är
 * verifierade mot elmarknad.se (följda omdirigeringar) och ska alltid vara
 * sidor som svarar 200.
 */
export const LEGACY_LINK_TARGETS: Record<string, string> = {
  "/elavtal/jamfor-elavtal": "/elavtal/jamfor",
  "/elavtal/pensionarer": "/elavtal/pensionar",
  "/elavtal/studenter": "/elavtal/student",
  "/elpriser/jamfor-elpriser": "/spotpris",
  "/dagens-spotpris": "/elpriser/idag",
  "/jamfor-offerter": "/offerter",
  "/offertforfragan": "/offerter",
  "/hitta-elnatsomrade": "/verktyg/hitta-elomrade",
  "/verktyg/hitta-elnatsomrade": "/verktyg/hitta-elomrade",
  "/kommun": "/elpriser",
  "/elskling": "/ordlista/elskling",
  "/elpriskollen": "/ordlista/elpriskollen",
  "/savera": "/ordlista/savera",
  "/compricer": "/ordlista/compricer",
  "/zmarta": "/ordlista/zmarta",
  "/elbruk": "/ordlista/elbruk",
};

const SITE_HOST = /^https?:\/\/(?:www\.)?elmarknad\.se(?=[/?#]|$)/i;

/** Slutdestination för en intern sökväg (utan query/hash), eller sökvägen själv. */
export function resolveInternalPath(path: string): string {
  const clean = path.length > 1 ? path.replace(/\/+$/, "") : path;
  const lower = clean.toLowerCase();
  return LEGACY_LINK_TARGETS[lower] ?? canonicalAliases[lower] ?? clean;
}

/**
 * Skriver om en länk-URL: elmarknad.se-länkar (även www) blir relativa,
 * avslutande "/" tas bort och gamla sökvägar pekas om till slutdestinationen.
 * Externa länkar, mailto:, ankare och filer lämnas orörda.
 */
export function rewriteInternalHref(href: string): string {
  const relative = href.replace(SITE_HOST, "") || "/";
  if (!relative.startsWith("/") || relative.startsWith("//")) return href;
  const match = /^([^?#]*)(.*)$/.exec(relative);
  const path = match?.[1] ?? relative;
  const rest = match?.[2] ?? "";
  // Filer (bilder, pdf, wp-content …) behåller sin adress.
  if (/\.[a-z0-9]{2,5}$/i.test(path) || path.startsWith("/wp-")) return href;
  return `${resolveInternalPath(path || "/")}${rest}`;
}

/** Skriver om alla markdown-länkar ([text](url "titel")) men inte bilder. */
export function rewriteMarkdownLinks(markdown: string): string {
  return markdown.replace(
    /(^|[^!])\[([^\]]*)\]\(\s*(<[^>]*>|[^\s)]+)((?:\s+"[^"]*")?\s*)\)/g,
    (_m, pre: string, text: string, rawUrl: string, title: string) => {
      const bracketed = rawUrl.startsWith("<") && rawUrl.endsWith(">");
      const url = bracketed ? rawUrl.slice(1, -1) : rawUrl;
      const next = rewriteInternalHref(url);
      // Titeln är ofta bara den gamla URL:en – den ger inget och tas bort.
      const keepTitle =
        title.trim() && !/^"https?:\/\//i.test(title.trim()) ? title : "";
      return `${pre}[${text}](${bracketed ? `<${next}>` : next}${keepTitle})`;
    },
  );
}
